import { Col } from 'react-bootstrap';
import { Card, CardBody } from 'reactstrap';
import CountUp from 'react-countup';
import React, { useRef, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { reverseDateToString } from '../../common/utils/date/Date.utils';

function DetailedSnapshotCard({
  title,
  prefix,
  value,
  icon,
  delta,
  deltaGrowth,
  footer,
  variant = 'primary',
  datePickerStartValue,
  datePickerEndValue,
  setDatePickerStartValue,
  setDatePickerEndValue,
  compareStartValue,
  compareEndValue,
  setCompareStartValue,
  setCompareEndValue,
  datePickerStartPlaceholder,
  datePickerEndPlaceholder,
  className,
  showDatePicker = false,
  showDateComparison = true,
  counterDecimals = 0,
}) {
  const datePickerRef = useRef(null);
  const activeSideRef = useRef('left');
  const [startRangeChanged, setStartRangeChanged] = useState(false);
  const [endRangeChanged, setEndRangeChanged] = useState(false);

  const formatRange = (start, end, fallback) => {
    if (start && end) {
      if (start !== end) return `${start} - ${end}`;
      return `${start}`;
    }
    if (fallback) return fallback;
    return '';
  };

  const leftDisplay = formatRange(datePickerStartValue, datePickerEndValue);
  const rightDisplay = formatRange(compareStartValue, compareEndValue);

  const openPickerFor = (side) => {
    activeSideRef.current = side;
    const picker = datePickerRef.current?.flatpickr;
    if (!picker) return;

    const currentText = side === 'left' ? leftDisplay : rightDisplay;
    const parts = (currentText || '').split('-').map((s) => s.trim());

    const parse = (s) => {
      const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s);
      if (!m) return null;
      // eslint-disable-next-line no-unused-vars
      const [_, dd, MM, yyyy] = m;
      return new Date(Number(yyyy), Number(MM) - 1, Number(dd));
    };

    const start = parse(parts[0] || '');
    const end = parse(parts[1] || '');

    if (start && end) {
      picker.setDate([start, end], false);
    } else {
      picker.clear();
    }

    picker.open();
  };

  const mapDeltaVariant = () => {
    if (deltaGrowth === true) return 'badge-soft-success';
    if (deltaGrowth === false) return 'badge-soft-danger';
    return 'bg-dark bg-opacity-10 dashboard__percent-change--zero';
  };

  return (
    <Col className={className}>
      <Card className="bg-light bg-opacity-30">
        <CardBody>
          <h5 title={title} className="mb-3 text-nowrap">
            {title}
          </h5>

          <div className="d-flex align-items-center gap-2">
            {icon && (
              <div className="avatar-md flex-shrink-0">
                <span
                  className={`avatar-title bg-${variant} bg-opacity-10 text-${variant} rounded-circle fs-22`}
                >
                  {icon}
                </span>
              </div>
            )}
            <h3 className="mb-0 fs-4">
              <CountUp
                prefix={prefix}
                end={value}
                decimals={counterDecimals}
                enableScrollSpy
                scrollSpyOnce
              />
            </h3>
            {delta && (
              <span className={`badge fw-medium ms-2 fs-xs ms-auto ${mapDeltaVariant()}`}>
                {delta}
              </span>
            )}
          </div>

          {footer && footer}

          {showDatePicker && (
            <p className="d-flex align-items-center gap-1 mb-0 mt-3">
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <span
                className="text-nowrap text-body-color fs-5 dashboard__detailed-snapshot-date-btn cursor-pointer"
                onClick={() => openPickerFor('left')}
              >
                {!startRangeChanged ? datePickerStartPlaceholder : leftDisplay}
              </span>

              {showDateComparison && (
                <span className="text-nowrap text-body-color fs-5"> vs </span>
              )}
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <span
                className="text-nowrap text-body-color fs-5 dashboard__detailed-snapshot-date-btn cursor-pointer"
                onClick={() => openPickerFor('right')}
              >
                {!endRangeChanged ? datePickerEndPlaceholder : rightDisplay}
              </span>

              <span className="ms-auto" />
              <Flatpickr
                ref={datePickerRef}
                className="visually-hidden mt-4"
                placeholder="Select a date range"
                options={{
                  mode: 'range',
                  dateFormat: 'd.m.Y',
                }}
                onChange={(selectedDates) => {
                  const [start, end] = selectedDates;
                  if (!start || !end) return;

                  const startStr = reverseDateToString(start);
                  const endStr = reverseDateToString(end);

                  if (activeSideRef.current === 'left') {
                    setDatePickerStartValue?.(startStr);
                    setDatePickerEndValue?.(endStr);
                    if (!startRangeChanged) setStartRangeChanged(true);
                  } else {
                    setCompareStartValue?.(startStr);
                    setCompareEndValue?.(endStr);
                    if (!endRangeChanged) setEndRangeChanged(true);
                  }
                }}
              />
            </p>
          )}
        </CardBody>
      </Card>
    </Col>
  );
}

export default DetailedSnapshotCard;
